import { PlanMeasureKey } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/types";
import { createContext, Dispatch, useMemo, useReducer } from "react";

type ColumnSeverity = "transparent" | "alarm" | "warning";

type AggregatedColumnSeverities = {
  [key in PlanMeasureKey]: {
    infrastructure: ColumnSeverity;
    train: ColumnSeverity;
  };
};

export type CountGroup = {
  [countKey in ColumnSeverity]: number;
};

export type ColumnCounts = {
  [key in PlanMeasureKey]: {
    infrastructure: CountGroup;
    train: CountGroup;
  };
};

type StateActions = {
  type: "add" | "remove";
  column: PlanMeasureKey;
  eventGroup: "train" | "infrastructure";
  severity: ColumnSeverity;
};

type ColumnSeverityContextValues = {
  countState: ColumnCounts;
  severityState: AggregatedColumnSeverities;
  dispatch: Dispatch<StateActions>;
};

function makeInitialCounts(): ColumnCounts {
  return {
    noCoverage: {
      infrastructure: { alarm: 0, warning: 0, transparent: 0 },
      train: { alarm: 0, warning: 0, transparent: 0 },
    },
    unpublished: {
      infrastructure: { alarm: 0, warning: 0, transparent: 0 },
      train: { alarm: 0, warning: 0, transparent: 0 },
    },
    published: {
      infrastructure: { alarm: 0, warning: 0, transparent: 0 },
      train: { alarm: 0, warning: 0, transparent: 0 },
    },
    withdrawn: {
      infrastructure: { alarm: 0, warning: 0, transparent: 0 },
      train: { alarm: 0, warning: 0, transparent: 0 },
    },
  };
}

function makeInitialContextValue(): ColumnSeverityContextValues {
  return {
    countState: makeInitialCounts(),
    severityState: {
      noCoverage: {
        infrastructure: "transparent",
        train: "transparent",
      },
      unpublished: {
        infrastructure: "transparent",
        train: "transparent",
      },
      published: {
        infrastructure: "transparent",
        train: "transparent",
      },
      withdrawn: {
        infrastructure: "transparent",
        train: "transparent",
      },
    },
    dispatch: (_: StateActions) => {},
  };
}

function reducer(state: ColumnCounts, action: StateActions): ColumnCounts {
  const column = state[action.column];
  const eventGroup = column[action.eventGroup];
  const severity = eventGroup[action.severity];

  let newCount = null;
  switch (action.type) {
    case "add":
      newCount = severity + 1;
      break;

    case "remove":
      newCount = severity - 1;
  }

  return {
    ...state,
    [action.column]: {
      ...column,
      [action.eventGroup]: {
        ...eventGroup,
        [action.severity]: newCount,
      },
    },
  };
}

function mapCountsToSeverity(state: ColumnCounts): AggregatedColumnSeverities {
  const columnEntries = Object.entries(state);

  const mappedColumnEntries = columnEntries.map(([columnKey, columnValue]) => {
    const groupEntries = Object.entries(columnValue);

    const mappedGroupEntries = groupEntries.map(([groupKey, groupValue]) => {
      if (groupValue.alarm) return [groupKey, "alarm"];
      if (groupValue.warning) return [groupKey, "warning"];
      return [groupKey, "transparent"];
    });

    return [columnKey, Object.fromEntries(mappedGroupEntries)];
  });

  return Object.fromEntries(mappedColumnEntries);
}

export function useMeasureOverviewColumnSeverityContextValues() {
  const [countState, dispatch] = useReducer(reducer, makeInitialCounts());

  const severityState: AggregatedColumnSeverities = useMemo(
    () => mapCountsToSeverity(countState),
    [countState],
  );

  return { countState, severityState, dispatch };
}

export const MeasureOverviewColumnSeverityContext =
  createContext<ColumnSeverityContextValues>(makeInitialContextValue());
